import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  onSnapshot,
  Timestamp,
  arrayUnion,
  arrayRemove,
  deleteField
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, storage } from '../config/firebase';
import { Task, ChecklistItem, TaskAttachment } from '../types';
import { postsService } from './postsService';
import {
  isBlocked as isBlockedFn,
  getBlockingTasks as getBlockingTasksFn,
  getBlockedByThis as getBlockedByThisFn,
  getSubTaskProgress as getSubTaskProgressFn,
  hasCircularDependency as hasCircularDependencyFn,
  canCompleteTask as canCompleteTaskFn,
  validateDependency,
} from '../utils/taskDependencies';

function safeToDate(value: any): Date | undefined {
  if (!value) return undefined;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  if (value.seconds != null) return new Date(value.seconds * 1000);
  return undefined;
}

// Shared Firestore-doc → Task mapper (timestamp conversion incl. completedAt/startDate).
function mapTaskDoc(d: any): Task {
  const data = d.data();
  return {
    id: d.id,
    ...data,
    dueDate: safeToDate(data.dueDate) || new Date(),
    startDate: safeToDate(data.startDate),
    createdAt: safeToDate(data.createdAt),
    updatedAt: safeToDate(data.updatedAt),
    completedAt: safeToDate(data.completedAt),
    visibility: data.visibility || 'private',
  } as Task;
}

export const tasksService = {
  // Get all tasks for current user (including public tasks)
  async getTasks(userId: string, orgId: string, userRole?: string, groupId?: string): Promise<Task[]> {
    try {
      const queries = [];
      
      // For admins and managers, get ALL tasks in the organization
      if (userRole === 'admin' || userRole === 'manager') {
        // Get all tasks in the organization (both public and private)
        let allOrgTasksQuery;
        if (groupId) {
          allOrgTasksQuery = query(
            collection(db, 'tasks'),
            where('orgId', '==', orgId),
            where('groupId', '==', groupId),
            orderBy('dueDate', 'asc')
          );
        } else {
          allOrgTasksQuery = query(
            collection(db, 'tasks'),
            where('orgId', '==', orgId),
            orderBy('dueDate', 'asc')
          );
        }
        queries.push(getDocs(allOrgTasksQuery));
      } else {
        // For regular users, get three sets of tasks:
        // 1. Their private tasks (assigned to them)
        let privateQuery;
        if (groupId) {
          privateQuery = query(
            collection(db, 'tasks'),
            where('assignedTo', '==', userId),
            where('visibility', '==', 'private'),
            where('groupId', '==', groupId),
            orderBy('dueDate', 'asc')
          );
        } else {
          privateQuery = query(
            collection(db, 'tasks'),
            where('assignedTo', '==', userId),
            where('visibility', '==', 'private'),
            orderBy('dueDate', 'asc')
          );
        }
        queries.push(getDocs(privateQuery));
        
        // 2. Public tasks in the organization
        let publicQuery;
        if (groupId) {
          publicQuery = query(
            collection(db, 'tasks'),
            where('orgId', '==', orgId),
            where('visibility', '==', 'public'),
            where('groupId', '==', groupId),
            orderBy('dueDate', 'asc')
          );
        } else {
          publicQuery = query(
            collection(db, 'tasks'),
            where('orgId', '==', orgId),
            where('visibility', '==', 'public'),
            orderBy('dueDate', 'asc')
          );
        }
        queries.push(getDocs(publicQuery));
        
        // 3. Tasks created by them (regardless of assignee or visibility)
        let createdByQuery;
        if (groupId) {
          createdByQuery = query(
            collection(db, 'tasks'),
            where('createdBy', '==', userId),
            where('groupId', '==', groupId),
            orderBy('dueDate', 'asc')
          );
        } else {
          createdByQuery = query(
            collection(db, 'tasks'),
            where('createdBy', '==', userId),
            orderBy('dueDate', 'asc')
          );
        }
        queries.push(getDocs(createdByQuery));
      }
      
      // Execute queries
      const snapshots = await Promise.all(queries);
      
      // Combine all results
      const allTasks: Task[] = [];
      snapshots.forEach(snapshot => {
        const tasks = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          dueDate: safeToDate(doc.data().dueDate) || new Date(),
          createdAt: safeToDate(doc.data().createdAt),
          updatedAt: safeToDate(doc.data().updatedAt),
          visibility: doc.data().visibility || 'private'
        })) as Task[];
        allTasks.push(...tasks);
      });
      
      // Remove duplicates using Map
      const taskMap = new Map<string, Task>();
      allTasks.forEach(task => {
        taskMap.set(task.id, task);
      });
      
      return Array.from(taskMap.values()).sort((a, b) => 
        new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
      );
    } catch (error) {
      console.error('Error getting tasks:', error);
      // Fallback to old query if visibility field doesn't exist yet
      try {
        const q = query(
          collection(db, 'tasks'),
          where('orgId', '==', orgId),
          where('assignedTo', '==', userId),
          orderBy('dueDate', 'asc')
        );
        
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          dueDate: safeToDate(doc.data().dueDate) || new Date(),
          visibility: 'private'
        })) as Task[];
      } catch (fallbackError) {
        console.error('Fallback query also failed:', fallbackError);
        throw fallbackError;
      }
    }
  },

  // Listen to tasks changes in real-time
  onTasksChange(userId: string, orgId: string, callback: (tasks: Task[]) => void, onError?: (error: any) => void, userRole?: string): () => void {
    // For all users, listen to ALL tasks in the organization
    // Filtering happens client-side to ensure users see tasks they created, were assigned, or are public
    const q = query(
      collection(db, 'tasks'),
      where('orgId', '==', orgId)
    );
    
    return onSnapshot(q, 
      (querySnapshot) => {
        try {
          const tasks = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            dueDate: safeToDate(doc.data().dueDate) || new Date(),
            createdAt: safeToDate(doc.data().createdAt),
            updatedAt: safeToDate(doc.data().updatedAt),
            visibility: doc.data().visibility || 'private'
          })) as Task[];
          
          // Additional filtering for non-admin users
          const filteredTasks = (userRole === 'admin' || userRole === 'manager')
            ? tasks
            : tasks.filter(task => 
                task.visibility === 'public' || 
                task.assignedTo === userId || 
                task.createdBy === userId
              );
          
          console.log('Successfully loaded', filteredTasks.length, 'tasks from Firestore');
          callback(filteredTasks);
        } catch (error) {
          console.error('Error processing tasks data:', error);
          onError?.(error);
        }
      },
      (error) => {
        console.error('Firestore tasks subscription error:', error);
        onError?.(error);
      }
    );
  },

  // Create new task
  async createTask(taskData: Omit<Task, 'id'> & { orgId?: string; createdBy?: string; createdByName?: string }): Promise<string> {
    try {
      const now = Timestamp.now();
      const newTask = {
        ...taskData,
        orgId: (taskData as any).orgId,
        createdBy: (taskData as any).createdBy || taskData.assignedTo,
        visibility: taskData.visibility || 'private', // Default to private if not specified
        dueDate: Timestamp.fromDate(taskData.dueDate),
        opportunityId: (taskData as any).opportunityId ?? null,
        createdAt: now,
        updatedAt: now
      };
      // Remove undefined (Firestore rejects them)
      Object.keys(newTask).forEach((k) => {
        if ((newTask as any)[k] === undefined) {
          delete (newTask as any)[k];
        }
      });
      const docRef = await addDoc(collection(db, 'tasks'), newTask as any);
      // Create system post for task creation
      try {
        if ((taskData as any).orgId) {
          await postsService.createSystemPost({
            orgId: (taskData as any).orgId!,
            type: 'task_created',
            createdById: (taskData as any).createdBy || 'system',
            createdByName: (taskData as any).createdByName || 'System',
            entityType: 'task',
            entityId: docRef.id,
            entityLabel: taskData.title
          });
        }
      } catch {}
      return docRef.id;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  },

  // Update task
  async updateTask(taskId: string, updates: Partial<Task>): Promise<void> {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.now()
      };
      
      // Only update dueDate if it's explicitly provided
      if ('dueDate' in updates && updates.dueDate) {
        updateData.dueDate = Timestamp.fromDate(updates.dueDate);
      }
      
      // Handle completion status change
      if ('completed' in updates) {
        const currentUser = auth.currentUser;
        if (updates.completed) {
          updateData.completedAt = Timestamp.now();
          updateData.completedBy = currentUser?.uid || 'unknown';
          updateData.completed = true;
        } else {
          // Only set completed to false, don't touch the other fields
          updateData.completed = false;
          // completedAt and completedBy stay in history
        }
      }

      // Clear blockedByTaskId in Firestore when explicitly nulled/emptied
      if (Object.prototype.hasOwnProperty.call(updates, 'blockedByTaskId')) {
        const v = (updates as { blockedByTaskId?: string | null }).blockedByTaskId;
        if (v === null || v === '') {
          updateData.blockedByTaskId = deleteField();
        }
      }
      
      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key as keyof typeof updateData] === undefined) {
          delete updateData[key as keyof typeof updateData];
        }
      });
      
      await updateDoc(taskRef, updateData);
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  },

  // Toggle task completion
  async toggleTaskCompletion(taskId: string, userId?: string): Promise<void> {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      const taskSnap = await getDoc(taskRef);
      if (!taskSnap.exists()) return;
      const currentTask = taskSnap.data() as any;
      const now = Timestamp.now();
      const currentUser = auth.currentUser;
      
      const updateData: any = {
        completed: !currentTask.completed,
        updatedAt: now
      };
      
      // Echobot celebration when completing a task
      if (!currentTask.completed && currentTask.orgId) {
        try {
          await postsService.createEchobotPost({
            orgId: currentTask.orgId,
            type: 'echobot_milestone',
            content: 'Super! Task erledigt! Weiter so! ✅',
            animation: 'celebrate'
          });

          // Check for 10 tasks completed milestone
          const allTasks = await this.getTasks(userId || currentUser?.uid || '', currentTask.orgId);
          const completedCount = allTasks.filter(t => t.completed).length;

          if (completedCount === 10) {
            await postsService.createEchobotPost({
              orgId: currentTask.orgId,
              type: 'echobot_milestone',
              content: '10 Tasks erledigt! Du bist produktiv! 🌟',
              animation: 'celebrate'
            });
          }

          if (completedCount === 50) {
            await postsService.createEchobotPost({
              orgId: currentTask.orgId,
              type: 'echobot_milestone',
              content: '50 Tasks geschafft! Incredible! 🚀',
              animation: 'celebrate'
            });
          }
        } catch (e) {
          console.error('Error creating Echobot milestone:', e);
        }
      }
      
      // Only add completedAt and completedBy when marking as complete
      if (!currentTask.completed) {
        updateData.completedAt = now;
        updateData.completedBy = userId || currentUser?.uid || 'unknown';
      }
      // When uncompleting, don't include these fields (they'll stay in history)
      
      await updateDoc(taskRef, updateData);
    } catch (error) {
      console.error('Error toggling task completion:', error);
      throw error;
    }
  },

  // Assign a task to a different user
  async assignTask(taskId: string, newAssigneeUserId: string): Promise<void> {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        assignedTo: newAssigneeUserId
      });
    } catch (error) {
      console.error('Error assigning task:', error);
      throw error;
    }
  },

  // Delete task
  async deleteTask(taskId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'tasks', taskId));
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  },

  // Check if user can edit task
  canEditTask(task: Task, userId: string, userRole?: 'admin' | 'manager' | 'sales_rep'): boolean {
    // Admin can edit all tasks
    if (userRole === 'admin') return true;
    
    // Manager can edit public tasks and their own tasks
    if (userRole === 'manager') {
      return task.visibility === 'public' || 
             task.assignedTo === userId || 
             task.createdBy === userId;
    }
    
    // Regular users can only edit their own tasks
    return task.assignedTo === userId || task.createdBy === userId;
  },

  // Check if user can delete task
  canDeleteTask(task: Task, userId: string, userRole?: 'admin' | 'manager' | 'sales_rep'): boolean {
    // Admin can delete all tasks
    if (userRole === 'admin') return true;
    
    // Manager can delete public tasks and their own tasks
    if (userRole === 'manager') {
      return task.visibility === 'public' || task.createdBy === userId;
    }
    
    // Regular users can only delete tasks they created
    return task.createdBy === userId;
  },

  // Get tasks by visibility
  async getTasksByVisibility(orgId: string, visibility: 'private' | 'public', userId?: string): Promise<Task[]> {
    try {
      let q;
      if (visibility === 'private' && userId) {
        q = query(
          collection(db, 'tasks'),
          where('visibility', '==', 'private'),
          where('assignedTo', '==', userId),
          orderBy('dueDate', 'asc')
        );
      } else if (visibility === 'public') {
        q = query(
          collection(db, 'tasks'),
          where('orgId', '==', orgId),
          where('visibility', '==', 'public'),
          orderBy('dueDate', 'asc')
        );
      } else {
        throw new Error('Invalid visibility or missing userId for private tasks');
      }
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dueDate: safeToDate(doc.data().dueDate) || new Date(),
        createdAt: safeToDate(doc.data().createdAt),
        updatedAt: safeToDate(doc.data().updatedAt)
      })) as Task[];
    } catch (error) {
      console.error('Error getting tasks by visibility:', error);
      throw error;
    }
  },

  // Get tasks for a specific engagement
  async getTasksByEngagement(engagementId: string, orgId: string): Promise<Task[]> {
    try {
      const q = query(
        collection(db, 'tasks'),
        where('engagementId', '==', engagementId),
        where('orgId', '==', orgId),
        orderBy('dueDate', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dueDate: safeToDate(doc.data().dueDate) || new Date(),
        createdAt: safeToDate(doc.data().createdAt),
        updatedAt: safeToDate(doc.data().updatedAt)
      })) as Task[];
    } catch (error) {
      console.error('Error getting tasks by engagement:', error);
      throw error;
    }
  },

  // Tasks linked to a company — targeted single-field query (no composite index),
  // org-scoped client-side for defense in depth.
  async getTasksByCompany(orgId: string, companyId: string): Promise<Task[]> {
    try {
      const snap = await getDocs(query(collection(db, 'tasks'), where('companyId', '==', companyId)));
      return snap.docs.map(mapTaskDoc).filter(t => t.orgId === orgId);
    } catch (error) {
      console.warn('Error getting tasks by company:', error);
      return [];
    }
  },

  // Tasks linked to a contact via leadId or contactId (merged + deduped).
  async getTasksByContact(orgId: string, contactId: string): Promise<Task[]> {
    try {
      const [byLead, byContact] = await Promise.all([
        getDocs(query(collection(db, 'tasks'), where('leadId', '==', contactId))),
        getDocs(query(collection(db, 'tasks'), where('contactId', '==', contactId))),
      ]);
      const map = new Map<string, Task>();
      [...byLead.docs, ...byContact.docs].forEach(d => {
        const t = mapTaskDoc(d);
        if (t.orgId === orgId) map.set(t.id, t);
      });
      return Array.from(map.values());
    } catch (error) {
      console.warn('Error getting tasks by contact:', error);
      return [];
    }
  },

  // Create a recurring task from template
  async createRecurringTask(
    engagementId: string,
    templateId: string,
    taskData: {
      title: string;
      description?: string;
      assignedTo: string;
      dueDate: Date;
      priority: 'low' | 'medium' | 'high';
      orgId: string;
      createdBy: string;
    }
  ): Promise<string> {
    try {
      const now = Timestamp.now();
      const newTask = {
        ...taskData,
        engagementId,
        recurringTemplateId: templateId,
        isRecurring: true,
        type: 'follow_up' as const,
        visibility: 'public' as const,
        completed: false,
        leadId: '',
        dueDate: Timestamp.fromDate(taskData.dueDate),
        createdAt: now,
        updatedAt: now
      };
      
      const docRef = await addDoc(collection(db, 'tasks'), newTask);
      return docRef.id;
    } catch (error) {
      console.error('Error creating recurring task:', error);
      throw error;
    }
  },

  // Create task linked to engagement
  async createEngagementTask(
    engagementId: string,
    milestoneId: string | undefined,
    taskData: Omit<Task, 'id' | 'engagementId' | 'milestoneId'>
  ): Promise<string> {
    try {
      const now = Timestamp.now();
      const newTask = {
        ...taskData,
        engagementId,
        milestoneId: milestoneId || null,
        dueDate: Timestamp.fromDate(taskData.dueDate),
        createdAt: now,
        updatedAt: now
      };
      
      // Remove undefined fields
      Object.keys(newTask).forEach(k => {
        if ((newTask as any)[k] === undefined) {
          delete (newTask as any)[k];
        }
      });
      
      const docRef = await addDoc(collection(db, 'tasks'), newTask);
      return docRef.id;
    } catch (error) {
      console.error('Error creating engagement task:', error);
      throw error;
    }
  },

  // ========== DEPENDENCY MANAGEMENT ==========

  async addDependency(taskId: string, dependsOnTaskId: string, allTasks?: Task[]): Promise<void> {
    try {
      // Defense in depth: when the caller can provide the task graph, reject
      // self-references and dependency cycles before writing.
      if (allTasks) {
        const validation = validateDependency(taskId, dependsOnTaskId, allTasks);
        if (!validation.ok) {
          throw new Error(validation.message);
        }
      }
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        dependsOn: arrayUnion(dependsOnTaskId),
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error adding dependency:', error);
      throw error;
    }
  },

  async removeDependency(taskId: string, dependsOnTaskId: string): Promise<void> {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        dependsOn: arrayRemove(dependsOnTaskId),
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error removing dependency:', error);
      throw error;
    }
  },

  // ========== SUB-TASK MANAGEMENT ==========

  async addSubTask(
    parentTaskId: string,
    subTaskData: Omit<Task, 'id' | 'parentTaskId'>
  ): Promise<string> {
    try {
      const now = Timestamp.now();
      const newSubTask = {
        ...subTaskData,
        parentTaskId,
        dueDate: Timestamp.fromDate(subTaskData.dueDate),
        createdAt: now,
        updatedAt: now
      };

      // Remove undefined fields
      Object.keys(newSubTask).forEach(k => {
        if ((newSubTask as any)[k] === undefined) {
          delete (newSubTask as any)[k];
        }
      });

      const docRef = await addDoc(collection(db, 'tasks'), newSubTask);

      // Update parent with new subtask ID
      const parentRef = doc(db, 'tasks', parentTaskId);
      await updateDoc(parentRef, {
        subtaskIds: arrayUnion(docRef.id),
        updatedAt: Timestamp.now()
      });

      return docRef.id;
    } catch (error) {
      console.error('Error adding sub-task:', error);
      throw error;
    }
  },

  async removeSubTask(parentTaskId: string, subTaskId: string): Promise<void> {
    try {
      // Remove from parent's subtaskIds
      const parentRef = doc(db, 'tasks', parentTaskId);
      await updateDoc(parentRef, {
        subtaskIds: arrayRemove(subTaskId),
        updatedAt: Timestamp.now()
      });

      // Clear parentTaskId from sub-task
      const subRef = doc(db, 'tasks', subTaskId);
      await updateDoc(subRef, {
        parentTaskId: null,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error removing sub-task:', error);
      throw error;
    }
  },

  // ========== CHECKLIST MANAGEMENT ==========

  // ========== ATTACHMENTS ==========

  /** Upload a file for a task and return the attachment metadata. */
  async uploadAttachment(orgId: string, taskId: string, file: File, uploadedBy?: string): Promise<TaskAttachment> {
    const safeName = file.name.replace(/[^\w.\-]+/g, '_');
    const path = `tasks/${orgId || 'default'}/${taskId}/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name: file.name,
      url,
      size: file.size,
      mimeType: file.type,
      uploadedAt: new Date(),
      uploadedBy,
    };
  },

  async updateChecklist(taskId: string, checklist: ChecklistItem[]): Promise<void> {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        checklist,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating checklist:', error);
      throw error;
    }
  },

  // ========== CLIENT-SIDE HELPER FUNCTIONS ==========

  isBlocked(task: Task, allTasks: Task[]): boolean {
    return isBlockedFn(task, allTasks);
  },

  getBlockingTasks(task: Task, allTasks: Task[]): Task[] {
    return getBlockingTasksFn(task, allTasks);
  },

  getBlockedByThis(task: Task, allTasks: Task[]): Task[] {
    return getBlockedByThisFn(task, allTasks);
  },

  getSubTaskProgress(task: Task, allTasks: Task[]): { completed: number; total: number } {
    return getSubTaskProgressFn(task, allTasks);
  },

  hasCircularDependency(taskId: string, targetDepId: string, allTasks: Task[]): boolean {
    return hasCircularDependencyFn(taskId, targetDepId, allTasks);
  },

  canCompleteTask(task: Task, allTasks: Task[]): boolean {
    return canCompleteTaskFn(task, allTasks);
  }
};